import React, { useState, useEffect, useMemo } from "react";
import SearchableSelect from "../common/SearchableSelect";
import { availableInventoryUnits, inventoryMatches } from "../../lib/inventorySelection";

/* ── Palette — matches CRM CSS variables ───────────────────── */
const C = {
  dark:        "#2563eb", // Lighter CRM blue (blue-600)
  dark2:       "#1d4ed8", // Lighter CRM blue (blue-700)
  green:       "#41b978",
  greenDark:   "#35a068",
  greenBg:     "#ecfbf3",
  greenText:   "#2a8a5a",
  red:         "#ff6f6f",
  redBg:       "#fff0f0",
  redText:     "#c0392b",
  amber:       "#d97706",
  amberBg:     "#fffbeb",
  blue:        "#5c85ff",
  blueBg:      "#eef2ff",
  border:      "#e6ebf5",
  borderStrong:"#d9e1ef",
  muted:       "#9aa5ba",
  text:        "#2f3950",
  white:       "#ffffff",
  bg:          "#fafbff",
  pillBg:      "rgba(255,255,255,0.12)",
  pillText:    "#e2e8f0",
};

/* ── Status config (mirrors CallModal) ─────────────────────── */
const STATUS = {
  ready:     { label: "Ready",      color: C.muted,  bg: "#f3f4f6", dot: "#9ca3af", pulse: false },
  dialing:   { label: "Dialing…",   color: C.amber,  bg: C.amberBg, dot: "#f59e0b", pulse: true  },
  dialed:    { label: "Dialed",     color: C.blue,   bg: C.blueBg,  dot: "#3b82f6", pulse: false },
  syncing:   { label: "Syncing…",   color: "#6d28d9",bg: "#f5f3ff", dot: "#8b5cf6", pulse: true  },
  connected: { label: "Connected",  color: C.green,  bg: C.greenBg, dot: "#22c55e", pulse: true  },
  dropped:   { label: "Disconnected",color: C.red,   bg: C.redBg,   dot: C.red,     pulse: false },
  error:     { label: "Error",      color: C.red,    bg: C.redBg,   dot: C.red,     pulse: false },
};

/* ── Tiny icons ─────────────────────────────────────────────── */
function PhoneIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
    </svg>
  );
}
function PhoneOffIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="2" y1="2" x2="22" y2="22"/>
      <path d="M16.5 16.5l-2-2M10 10l-2-2M4.86 4.86C3.1 6.62 2 9.18 2 12c0 1.66.4 3.22 1.12 4.59l1.44-1.44A8.96 8.96 0 013 12c0-2.25.83-4.3 2.19-5.87L4.86 4.86zM12 6a5.97 5.97 0 014.24 1.76L18 6c-1.6-1.6-3.81-2.59-6.24-2.59-.88 0-1.72.14-2.52.4l1.52 1.52C11.17 5.11 11.58 5.05 12 5v1z"/>
    </svg>
  );
}
function SyncIcon({ spin }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13"
      style={spin ? { animation: "spin 1s linear infinite" } : {}}>
      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/>
    </svg>
  );
}
function SaveIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
    </svg>
  );
}
function WhatsAppIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.945C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
    </svg>
  );
}

/* ── Status dot with optional pulse ────────────────────────── */
function Dot({ color, pulse, style }) {
  return (
    <span style={{
      display: "inline-block",
      width: 10, height: 10,
      borderRadius: "50%",
      background: color,
      flexShrink: 0,
      animation: pulse ? "pulse 1.4s ease-in-out infinite" : "none",
      ...style,
    }} />
  );
}

/* ── Main component ─────────────────────────────────────────── */
export default function CallDispositionPopup({ lead, agents, defaultAgentId, lockAgent, dispositionGroups, fetchJson, onStartCall, onSyncCall, onSaveDisposition, onDisconnectCall, isAdmin }) {
  const [agentId, setAgentId]           = useState(defaultAgentId ? String(defaultAgentId) : "");
  const [callLogId, setCallLogId]       = useState("");
  const [stage, setStage]               = useState("ready"); // ready | dialing | dialed | syncing | dropped | error
  const [msg, setMsg]                   = useState({ text: "", ok: true });
  const [ucid, setUcid]                 = useState(null);
  const [form, setForm]                 = useState({
    dispositionId: "", subDispositionId: "", nextFollowUp: "", remarks: "",
  });

  const [invData, setInvData]             = useState(null);
  const [invError, setInvError]           = useState("");
  const [invFilterCity, setInvFilterCity]     = useState("");
  const [invFilterDealer, setInvFilterDealer] = useState("");
  const [invFilterOem, setInvFilterOem]   = useState("");
  const [sameDayConfirmOpen, setSameDayConfirmOpen] = useState(false);
  const [toast, setToast]   = useState({ text: "", ok: true, visible: false });
  const toastTimerRef = React.useRef(null);
  const [satisfiedChoice, setSatisfiedChoice] = useState(null);
  // Booking / delivery extra fields
  const [bdCity,   setBdCity]   = useState(lead?.city_name || "");
  const [bdOem,    setBdOem]    = useState("");
  const [bdDealer, setBdDealer] = useState("");
  const [bdDate,   setBdDate]   = useState("");
  const [tdBooked, setTdBooked] = useState(false);   // test drive booked -> show WhatsApp button
  const [waSending, setWaSending] = useState(false);
  const [waPreview, setWaPreview] = useState(null);  // { message, phone } -> opens preview modal
  const [waLoadingPreview, setWaLoadingPreview] = useState(false);

  function showToast(text, ok = true) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ text, ok, visible: true });
    toastTimerRef.current = setTimeout(() => setToast((t) => ({ ...t, visible: false })), 3500);
  }

  const dispIdToGroupSlug = useMemo(() => {
    const map = {};
    (dispositionGroups || []).forEach((g) => {
      const addAll = (disps) => disps.forEach((d) => {
        map[String(d.id)] = g.slug || "";
        if (d.children) addAll(d.children);
      });
      addAll(g.dispositions || []);
    });
    return map;
  }, [dispositionGroups]);

  const rootDispositions = (dispositionGroups || []).map((g) => g.dispositions?.[0]).filter(Boolean);
  const selectedRoot     = rootDispositions.find((d) => String(d.id) === String(form.dispositionId));
  const subDispositions  = selectedRoot?.children || [];
  const selectedSub      = subDispositions.find((d) => String(d.id) === String(form.subDispositionId));
  const requiresFollowUp = Boolean(selectedRoot?.requires_follow_up || selectedSub?.requires_follow_up);

  const activeDispId = form.subDispositionId || form.dispositionId;
  const activeGroupSlug = dispIdToGroupSlug[String(activeDispId)] || "";

  const isTestDriveConducted = activeGroupSlug === "test-drive-conducted" || Boolean(
    (selectedRoot?.name || "").toLowerCase().includes("test drive conducted") ||
    (selectedSub?.name  || "").toLowerCase().includes("test drive conducted"),
  );
  const isTestDrive = !isTestDriveConducted && (
    activeGroupSlug === "interested-for-test-drive" || Boolean(
      (selectedRoot?.name || "").toLowerCase().includes("test drive") ||
      (selectedSub?.name  || "").toLowerCase().includes("test drive"),
    )
  );
  const showInventory = isTestDrive || (isTestDriveConducted && satisfiedChoice === false);

  const isBookedDisposition   = ["pre-booked", "appointment"].includes(activeGroupSlug);
  const isDeliveredDisposition = ["vehicle-onboardeddelivered", "vehicle-onboarded-delivered", "delivered"].includes(activeGroupSlug);
  const showBookingFields = isBookedDisposition || isDeliveredDisposition;

  useEffect(() => { setSatisfiedChoice(null); }, [form.dispositionId, form.subDispositionId]);
  // Reset booking/delivery + inventory filters when disposition changes
  useEffect(() => {
    setBdCity(lead?.city_name || "");
    setBdOem("");
    setBdDealer("");
    setBdDate("");
    setInvFilterDealer("");
    setInvFilterOem("");
    setInvFilterCity("");
  }, [form.dispositionId, form.subDispositionId]);

  useEffect(() => {
    if (!(isTestDrive || isTestDriveConducted || showBookingFields) || !fetchJson) return;
    let active = true;
    setInvData(null);
    setInvError("");
    fetchJson("/api/crm/inventory/", { cache: "no-store" })
      .then((data) => { if (active) setInvData(data); })
      .catch((error) => { if (active) setInvError(error.message || "Could not load inventory."); });
    return () => { active = false; };
  }, [isTestDrive, isTestDriveConducted, showBookingFields, fetchJson]);

  function clearInventoryFilters() {
    setInvFilterCity("");
    setInvFilterOem("");
    setInvFilterDealer("");
  }

  const invRows = invData?.rows || [];

  // Fully bidirectional cascading — each field's options reflect the other two selections
  const invCityOptions = useMemo(() => (
    [...new Set(
      invRows
        .filter((r) => inventoryMatches(r, { oem: invFilterOem, dealer: invFilterDealer }))
        .map((r) => r.city).filter(Boolean)
    )].sort()
  ), [invRows, invFilterOem, invFilterDealer]);

  const invOemOptions = useMemo(() => (
    [...new Set(
      invRows
        .filter((r) => inventoryMatches(r, { city: invFilterCity, dealer: invFilterDealer }))
        .map((r) => r.oem).filter(Boolean)
    )].sort()
  ), [invRows, invFilterCity, invFilterDealer]);

  const invDealerOptions = useMemo(() => (
    [...new Set(
      invRows
        .filter((r) => inventoryMatches(r, { city: invFilterCity, oem: invFilterOem }))
        .map((r) => r.dealer).filter(Boolean)
    )].sort()
  ), [invRows, invFilterCity, invFilterOem]);

  const invUnits = useMemo(
    () => availableInventoryUnits(invRows, {
      city: invFilterCity, oem: invFilterOem, dealer: invFilterDealer,
    }),
    [invRows, invFilterCity, invFilterDealer, invFilterOem],
  );

  const statusKey = stage === "dialing" ? "dialing"
    : stage === "dialed"   ? "dialed"
    : stage === "syncing"  ? "syncing"
    : stage === "dropped"  ? "dropped"
    : stage === "error"    ? "error"
    : callLogId            ? "connected"
    : "ready";

  const status = STATUS[statusKey];

  const initials = (lead?.full_name || "")
    .split(" ").filter(Boolean).slice(0, 2)
    .map((p) => p[0]).join("").toUpperCase();

  useEffect(() => {
    let intervalId;
    if (callLogId && !ucid && stage === "dialed") {
      intervalId = setInterval(() => {
        handleSync(true);
      }, 3000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [callLogId, ucid, stage]);

  async function handleCall() {
    setStage("dialing");
    setMsg({ text: "", ok: true });
    try {
      const payload = await onStartCall(lead.id, agentId || undefined);
      const logId = String(payload.call_log_id || "");
      setCallLogId(logId);
      if (payload.agent?.id) setAgentId(String(payload.agent.id));
      setStage("dialed");
      setMsg({ text: "Call sent to Ozonetel. Sync after the call to load disposition details.", ok: true });
    } catch (e) {
      setStage("error");
      setMsg({ text: e.message || "Failed to start call.", ok: false });
    }
  }

  async function handleSync(silent = false) {
    if (!silent) setStage("syncing");
    if (!silent) setMsg({ text: "", ok: true });
    try {
      const detail = await onSyncCall(lead.id);
      const log = (detail?.call_logs || []).find((l) => String(l.id) === callLogId)
        || (detail?.call_logs || [])[0];
      if (log) {
        if (log.ucid) setUcid(log.ucid);
        setForm((prev) => ({
          ...prev,
          dispositionId:    String(log.disposition?.id    || prev.dispositionId),
          subDispositionId: String(log.sub_disposition?.id || prev.subDispositionId),
          remarks:          log.remarks || prev.remarks,
        }));
        if (!silent || log.ucid) {
          setMsg({ text: log.ucid ? "Call connected! Disconnect enabled." : "Synced latest call details.", ok: true });
        }
      } else {
        if (!silent) setMsg({ text: "No call data found yet. Try again after the call ends.", ok: true });
      }
      if (!silent) setStage(callLogId ? "dialed" : "ready");
    } catch (e) {
      if (!silent) {
        setStage("error");
        setMsg({ text: e.message || "Sync failed.", ok: false });
      }
    }
  }

  async function handleDisconnect() {
    setMsg({ text: "", ok: true });
    try {
      await onDisconnectCall(lead.id, callLogId);
      setStage("dropped");
      setMsg({ text: "Hangup sent to Ozonetel — call disconnected.", ok: true });
    } catch (e) {
      setMsg({ text: e.message || "Failed to disconnect call via Ozonetel.", ok: false });
    }
  }

  async function doSave() {
    setSameDayConfirmOpen(false);
    try {
      const ok = await onSaveDisposition(lead.id, {
        ...form,
        callLogId,
        agentId,
        scheduleOem:          showInventory ? invFilterOem    : "",
        scheduleDealer:       showInventory ? invFilterDealer : "",
        scheduleCity:         showInventory ? invFilterCity   : "",
        testDriveConducted:   isTestDriveConducted,
        satisfied:            isTestDriveConducted ? satisfiedChoice : null,
        isBookedDisposition,
        isDeliveredDisposition,
        bdOem,
        bdDealer,
        bdCity,
        bdDate,
      });
      if (ok) {
        const toastMsg = isTestDriveConducted
          ? satisfiedChoice
            ? "Test drive outcome saved - customer satisfied! ✅"
            : "Test drive logged. Rescheduled next test drive! 🔄"
          : isTestDrive
            ? "Test drive booked successfully! 🎉"
            : "Disposition saved!";
        showToast(toastMsg);
        if (isTestDrive) setTdBooked(true);
      }
    } catch (e) {
      showToast(e.message || "Save failed.", false);
    }
  }

  async function openWhatsAppPreview() {
    if (!fetchJson) return;
    setWaLoadingPreview(true);
    try {
      const data = await fetchJson(`/api/crm/leads/${lead.id}/send-td-confirmation/`);
      setWaPreview(data);
    } catch (e) {
      showToast(e.message || "Could not load preview.", false);
    } finally {
      setWaLoadingPreview(false);
    }
  }

  async function confirmSendWhatsApp() {
    if (!fetchJson) return;
    setWaSending(true);
    try {
      await fetchJson(`/api/crm/leads/${lead.id}/send-td-confirmation/`, { method: "POST", body: "{}" });
      setWaPreview(null);
      showToast("WhatsApp confirmation sent to customer ✅");
    } catch (e) {
      showToast(e.message || "Failed to send WhatsApp.", false);
    } finally {
      setWaSending(false);
    }
  }

  async function handleSave() {
    if (isTestDriveConducted && satisfiedChoice === null) {
      showToast("Please select whether the customer was satisfied.", false); return;
    }
    if (showInventory) {
      if (!form.nextFollowUp) {
        showToast("Please select a Schedule Date.", false); return;
      }
      if (!invFilterCity) {
        showToast("Please select a City.", false); return;
      }
      if (!invFilterOem) {
        showToast("Please select an OEM.", false); return;
      }
      if (!invFilterDealer) {
        showToast("Please select a Dealer.", false); return;
      }
      // Check for same-day re-booking
      const existingDate = lead?.custom_fields?.test_drive_date;
      if (existingDate && form.nextFollowUp) {
        const existingDay = new Date(existingDate).toDateString();
        const newDay      = new Date(form.nextFollowUp).toDateString();
        if (existingDay === newDay) {
          setSameDayConfirmOpen(true);
          return;
        }
      }
    }
    await doSave();
  }

  function setField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "dispositionId" ? { subDispositionId: "" } : {}),
    }));
  }

  const isDialing  = stage === "dialing";
  const isDialed   = stage === "dialed";
  const isSyncing  = stage === "syncing";
  const hasNoPhone = !lead?.phone_number;

  return (
    <>
      <style>{`
        @keyframes pulse       { 0%,100%{opacity:1} 50%{opacity:.4} }
        @keyframes spin        { to{transform:rotate(360deg)} }
        @keyframes toastSlideIn{ from{opacity:0;transform:translateX(-50%) translateY(-12px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }
        @keyframes toastFadeOut{ from{opacity:1} to{opacity:0} }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
               font-size: 14px; color: ${C.text}; background: ${C.bg}; overflow-y: auto; }
        select, input, textarea, button { font-family: inherit; font-size: 14px; }
        select:focus, input:focus, textarea:focus { outline: 2px solid #3b82f6; outline-offset: 1px; }
      `}</style>

      {/* ── WhatsApp preview modal ── */}
      {waPreview && (
        <div
          onClick={() => !waSending && setWaPreview(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 380, overflow: "hidden", boxShadow: "0 12px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ background: "linear-gradient(135deg,#25D366,#1da851)", color: "#fff", padding: "12px 16px", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
              💬 WhatsApp Preview
            </div>
            <div style={{ padding: 16 }}>
              <p style={{ margin: "0 0 8px", fontSize: 12, color: "#6b7280" }}>
                To: <strong style={{ color: "#1a2b4a" }}>+{waPreview.phone || "—"}</strong>
              </p>
              <div style={{ background: "#dcf8c6", borderRadius: 10, padding: "10px 12px", fontSize: 13, lineHeight: 1.5, color: "#1a2b4a", whiteSpace: "pre-wrap", border: "1px solid #c5ead0" }}>
                {waPreview.message}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button
                  type="button"
                  onClick={() => setWaPreview(null)}
                  disabled={waSending}
                  style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmSendWhatsApp}
                  disabled={waSending}
                  style={{ flex: 2, padding: "9px 0", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#25D366,#1da851)", color: "#fff", fontWeight: 700, fontSize: 13, cursor: waSending ? "default" : "pointer", opacity: waSending ? 0.6 : 1 }}
                >
                  {waSending ? "Sending…" : "Send Now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast notification ── */}
      {toast.visible && (
        <div style={{
          position: "fixed", top: 16, left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
          display: "flex", alignItems: "center", gap: 9,
          padding: "11px 18px", borderRadius: 12,
          boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
          background: toast.ok
            ? "linear-gradient(135deg,#16a34a,#15803d)"
            : "linear-gradient(135deg,#dc2626,#b91c1c)",
          color: "#fff",
          fontSize: 13, fontWeight: 600,
          animation: "toastSlideIn 0.25s ease",
          whiteSpace: "nowrap",
          maxWidth: "calc(100vw - 32px)",
        }}>
          {toast.ok ? (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0 }}>
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          {toast.text}
          <button
            onClick={() => setToast((t) => ({ ...t, visible: false }))}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.7)", cursor: "pointer", padding: "0 0 0 4px", fontSize: 16, lineHeight: 1 }}
          >×</button>
        </div>
      )}

      {/* ── Header ── */}
      <div style={{
        background: `linear-gradient(135deg, ${C.dark} 0%, ${C.dark2} 100%)`,
        borderBottom: `3px solid ${C.blue}`,
        color: C.white, padding: "18px 20px 14px",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ position: "relative" }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: 17, color: C.white,
              }}>{initials || "?"}</div>
              <Dot color={status.dot} pulse={status.pulse} style={{
                position: "absolute", bottom: 1, right: 1,
                border: `2px solid ${C.dark}`,
              }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 17 }}>{lead?.full_name || "Unknown"}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
                <PhoneIcon />
                <span>{hasNoPhone ? "No phone number" : lead.phone_number}</span>
                {lead?.client_id && <span style={{ opacity: 0.55 }}>· {lead.client_id}</span>}
              </div>
            </div>
          </div>
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.05em",
            padding: "4px 12px", borderRadius: 999,
            background: status.bg, color: status.color,
          }}>{status.label}</span>
        </div>

        {/* Chips */}
        {(lead?.source_name || lead?.city_name || lead?.current_disposition_name) && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {lead.source_name && (
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: C.pillBg, color: C.pillText }}>{lead.source_name}</span>
            )}
            {lead.city_name && (
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: C.pillBg, color: C.pillText }}>{lead.city_name}</span>
            )}
            {lead.current_disposition_name && (
              <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(59,130,246,0.25)", color: "#93c5fd" }}>{lead.current_disposition_name}</span>
            )}
          </div>
        )}
      </div>


      <div style={{ padding: "14px 16px 0" }}>

        {/* ── Agent selector ── */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 5 }}>
            Calling Agent
          </label>
          {lockAgent ? (
            <div style={{
              width: "100%", padding: "8px 10px", borderRadius: 8,
              border: `1.5px solid ${C.border}`, background: "#f8fafc",
              fontSize: 14, color: C.text, fontWeight: 500,
            }}>
              {(agents || []).find((a) => String(a.id) === String(defaultAgentId))?.full_name || "You"}
            </div>
          ) : (
            <select
              style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white }}
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              disabled={isDialing || stage === "dialed"}
            >
              <option value="">Choose agent…</option>
              {(agents || []).map((a) => (
                <option key={a.id} value={a.id}>{a.full_name}</option>
              ))}
            </select>
          )}
        </div>

        {/* ── Call / Disconnect / Sync row ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
          <button
            onClick={handleCall}
            disabled={isDialing || isDialed || hasNoPhone}
            style={{
              flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              padding: "10px 14px", borderRadius: 10, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13,
              background: isDialing
                ? "#4ade80"
                : `linear-gradient(135deg, ${C.green}, ${C.greenDark})`,
              color: C.white,
              boxShadow: "0 2px 8px rgba(22,163,74,0.3)",
              opacity: (hasNoPhone || isDialed) ? 0.5 : 1,
            }}
          >
            <PhoneIcon />
            {isDialing ? "Dialing…" : "Call Now"}
          </button>

          <button
            onClick={handleDisconnect}
            disabled={!ucid || stage === "dropped"}
            style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "10px 10px", borderRadius: 10, border: "none", cursor: "pointer",
              fontWeight: 700, fontSize: 13,
              background: C.redBg, color: C.red,
              opacity: (!ucid || stage === "dropped") ? 0.45 : 1,
            }}
          >
            <PhoneOffIcon />
            Disconnect
          </button>

          <button
            onClick={handleSync}
            disabled={isSyncing}
            title="Sync call status from Ozonetel"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              padding: "10px 14px", borderRadius: 10,
              border: `1.5px solid ${C.border}`, background: C.white,
              fontWeight: 600, fontSize: 13, color: C.muted, cursor: "pointer",
            }}
          >
            <SyncIcon spin={isSyncing} />
            {isSyncing ? "Syncing…" : "Sync"}
          </button>
        </div>

        {/* ── Divider ── */}
        <div style={{ height: 1, background: C.border, margin: "0 0 14px" }} />

        {/* ── Disposition form ── */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 5 }}>
            Disposition
          </label>
          <select
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white }}
            value={form.dispositionId}
            onChange={(e) => setField("dispositionId", e.target.value)}
          >
            <option value="">Select disposition…</option>
            {rootDispositions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        <div style={{ display: subDispositions.length > 0 ? "block" : "none", marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 5 }}>
            Sub-Disposition
          </label>
          <select
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white }}
            value={form.subDispositionId}
            onChange={(e) => setField("subDispositionId", e.target.value)}
          >
            <option value="">Select sub-disposition…</option>
            {subDispositions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        {/* ── Test Drive Conducted: Satisfied? ── */}
        {isTestDriveConducted && (
          <div style={{ marginBottom: 14, padding: "12px 14px", borderRadius: 10, border: `1.5px solid ${C.borderStrong}`, background: "#f8fafc" }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 10 }}>
              Was the customer satisfied?
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                onClick={() => setSatisfiedChoice(true)}
                style={{
                  flex: 1, padding: "9px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", border: "1.5px solid",
                  background: satisfiedChoice === true ? "#dcfce7" : "#fff",
                  color: satisfiedChoice === true ? "#15803d" : "#6b7280",
                  borderColor: satisfiedChoice === true ? "#86efac" : C.border,
                }}
              >✅ Yes, Satisfied</button>
              <button
                type="button"
                onClick={() => setSatisfiedChoice(false)}
                style={{
                  flex: 1, padding: "9px 0", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", border: "1.5px solid",
                  background: satisfiedChoice === false ? "#fef2f2" : "#fff",
                  color: satisfiedChoice === false ? "#dc2626" : "#6b7280",
                  borderColor: satisfiedChoice === false ? "#fca5a5" : C.border,
                }}
              >🔄 No, Reschedule</button>
            </div>
            {satisfiedChoice === false && (
              <p style={{ marginTop: 8, fontSize: 12, color: "#b45309", fontWeight: 500 }}>
                Book next test drive below ↓
              </p>
            )}
          </div>
        )}

        {/* ── Booking / Delivery details card ── */}
        {showBookingFields && (() => {
          const accent     = isDeliveredDisposition ? "#7c3aed" : "#d97706";
          const accentBg   = isDeliveredDisposition ? "#f5f3ff" : "#fffbeb";
          const accentBorder = isDeliveredDisposition ? "#ddd6fe" : "#fde68a";
          const accentDark = isDeliveredDisposition ? "#6d28d9" : "#b45309";
          const cardLabel  = isDeliveredDisposition ? "DELIVERY DETAILS" : "BOOKING DETAILS";
          const bdRows = invData?.rows || [];
          // Bidirectional: each dropdown's options respect the other two selections
          const bdCityOpts   = [...new Set(bdRows.filter(r => (!bdOem || r.oem === bdOem) && (!bdDealer || r.dealer === bdDealer)).map(r => r.city).filter(Boolean))].sort();
          const bdOemOpts    = [...new Set(bdRows.filter(r => (!bdCity || r.city === bdCity) && (!bdDealer || r.dealer === bdDealer)).map(r => r.oem).filter(Boolean))].sort();
          const bdDealerOpts = [...new Set(bdRows.filter(r => (!bdCity || r.city === bdCity) && (!bdOem || r.oem === bdOem)).map(r => r.dealer).filter(Boolean))].sort();
          const sel = { width: "100%", padding: "7px 9px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white, fontSize: 13 };
          return (
            <div style={{ marginBottom: 14, borderRadius: 10, border: `1.5px solid ${accentBorder}`, background: accentBg, overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: `linear-gradient(135deg,${accent},${accentDark})`, color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase" }}>
                {cardLabel}
              </div>
              <div style={{ padding: "10px 12px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {/* City */}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: accent, marginBottom: 4 }}>📍 City</label>
                  <SearchableSelect style={sel} value={bdCity} onChange={setBdCity} options={bdCityOpts} placeholder="Select city…" />
                </div>
                {/* OEM */}
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: accent, marginBottom: 4 }}>🏭 OEM</label>
                  <select style={sel} value={bdOem} onChange={e => setBdOem(e.target.value)}>
                    <option value="">Select OEM…</option>
                    {bdOemOpts.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                {/* Dealer */}
                <div>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: accent, marginBottom: 4 }}>🏢 Dealer</label>
                  <select style={sel} value={bdDealer} onChange={e => setBdDealer(e.target.value)}>
                    <option value="">Select dealer…</option>
                    {bdDealerOpts.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                {/* Date */}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: accent, marginBottom: 4 }}>
                    📅 {isDeliveredDisposition ? "Delivery Date" : "Booking Date"}
                  </label>
                  <input
                    type="date"
                    style={{ ...sel, width: "auto" }}
                    value={bdDate}
                    onChange={e => setBdDate(e.target.value)}
                  />
                </div>
              </div>
            </div>
          );
        })()}

        <div style={{ display: (requiresFollowUp || showInventory) ? "block" : "none", marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 5 }}>
            {showInventory ? "Schedule Date" : subDispositions.length === 0 ? "Date" : "Next Follow-Up"}
          </label>
          <input
            type="datetime-local"
            style={{ width: "auto", padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`, background: C.white }}
            min={isAdmin ? undefined : new Date().toISOString().slice(0, 16)}
            value={form.nextFollowUp}
            onChange={(e) => setField("nextFollowUp", e.target.value)}
          />
        </div>

        {/* ── Inventory card (test drive / not-satisfied reschedule) ── */}
        {showInventory && (
          <div style={{
            marginBottom: 14, borderRadius: 10,
            border: `1.5px solid ${C.borderStrong}`,
            background: C.blueBg, overflow: "hidden",
          }}>
            <div style={{
              padding: "8px 12px", background: C.blue, color: C.white,
              fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
              display: "flex", alignItems: "center", justifyContent: "space-between",
            }}>
              <span>Inventory</span>
              {(invFilterCity || invFilterOem || invFilterDealer) && (
                <button
                  type="button"
                  onClick={clearInventoryFilters}
                  style={{ background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 6, cursor: "pointer", letterSpacing: 0, textTransform: "none" }}
                >
                  Clear
                </button>
              )}
            </div>
            <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8 }}>
              {!invData ? (
                <p style={{ fontSize: 12, color: invError ? C.red : C.muted, margin: 0 }}>{invError || "Loading inventory…"}</p>
              ) : (
                <>
                  {/* City → OEM → Dealer — all from inventory, fully bidirectional, any order */}
                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 4 }}>
                      City <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <SearchableSelect
                      style={{ border: `1.5px solid ${invFilterCity ? "#3b82f6" : C.border}` }}
                      value={invFilterCity}
                      onChange={setInvFilterCity}
                      options={invCityOptions}
                      placeholder="Select City"
                    />
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 4 }}>
                      OEM <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <select
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 7, border: `1.5px solid ${invFilterOem ? "#3b82f6" : C.border}`, background: C.white, fontSize: 13 }}
                      value={invFilterOem}
                      onChange={(e) => setInvFilterOem(e.target.value)}
                    >
                      <option value="">Select OEM</option>
                      {invOemOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.dark, marginBottom: 4 }}>
                      Dealer <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <select
                      style={{ width: "100%", padding: "6px 8px", borderRadius: 7, border: `1.5px solid ${invFilterDealer ? "#3b82f6" : C.border}`, background: C.white, fontSize: 13 }}
                      value={invFilterDealer}
                      onChange={(e) => setInvFilterDealer(e.target.value)}
                    >
                      <option value="">Select Dealer</option>
                      {invDealerOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>

                  {/* ── Available units ── */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 10px", borderRadius: 8, background: C.white, border: `1.5px solid ${invUnits === 0 ? "#f87171" : C.border}` }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.muted }}>Available Units</span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: invUnits === 0 ? "#ef4444" : C.dark }}>{invUnits}</span>
                  </div>

                  {/* WhatsApp confirmation — shown after the test drive is booked. */}
                  {tdBooked && (
                    <button
                      type="button"
                      onClick={openWhatsAppPreview}
                      disabled={waLoadingPreview}
                      style={{
                        marginTop: 8, width: "100%", display: "flex", alignItems: "center",
                        justifyContent: "center", gap: 8, padding: "9px 12px", borderRadius: 8,
                        border: "none", cursor: waLoadingPreview ? "default" : "pointer",
                        fontWeight: 700, fontSize: 13, color: "#fff",
                        background: "linear-gradient(135deg,#25D366,#1da851)",
                        opacity: waLoadingPreview ? 0.6 : 1,
                      }}
                    >
                      {waLoadingPreview ? "Loading…" : (<><WhatsAppIcon /> Send WhatsApp Confirmation</>)}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.muted, marginBottom: 5 }}>
            Remarks
          </label>
          <textarea
            style={{
              width: "100%", padding: "8px 10px", borderRadius: 8, border: `1.5px solid ${C.border}`,
              background: C.white, resize: "vertical", minHeight: 70, fontFamily: "inherit", fontSize: 13,
            }}
            placeholder="Add call notes…"
            value={form.remarks}
            onChange={(e) => setField("remarks", e.target.value)}
          />
        </div>

        {/* ── Same-day re-booking confirmation ── */}
        {sameDayConfirmOpen && (
          <div style={{
            marginBottom: 12, borderRadius: 10, border: "1.5px solid #f59e0b",
            background: "#fffbeb", overflow: "hidden",
          }}>
            <div style={{
              padding: "10px 14px", background: "#f59e0b", color: "#fff",
              fontSize: 12, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase",
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Already booked for today
            </div>
            <div style={{ padding: "12px 14px" }}>
              <p style={{ fontSize: 13, color: "#92400e", margin: "0 0 12px", lineHeight: 1.5 }}>
                A test drive is already booked for <strong>today</strong>. Are you sure you want to book another one for the same day?
              </p>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={doSave}
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                    fontWeight: 700, fontSize: 13,
                    background: "linear-gradient(135deg, #f59e0b, #d97706)", color: "#fff",
                  }}
                >
                  Yes, Book Again
                </button>
                <button
                  type="button"
                  onClick={() => setSameDayConfirmOpen(false)}
                  style={{
                    flex: 1, padding: "8px 12px", borderRadius: 8, cursor: "pointer",
                    fontWeight: 600, fontSize: 13,
                    background: "#fff", border: "1.5px solid #d1d5db", color: C.text,
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Save ── */}
        <button
          onClick={handleSave}
          style={{
            width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            padding: "12px", borderRadius: 10, border: "none", cursor: "pointer",
            fontWeight: 700, fontSize: 14,
            background: showInventory
              ? `linear-gradient(135deg, #5c85ff, #9b79ff)`
              : isTestDriveConducted
                ? satisfiedChoice === true
                  ? `linear-gradient(135deg, ${C.green}, ${C.greenDark})`
                  : `linear-gradient(135deg, #f59e0b, #d97706)`
                : `linear-gradient(135deg, ${C.green}, ${C.greenDark})`,
            color: C.white,
            boxShadow: showInventory
              ? "0 2px 10px rgba(92,133,255,0.35)"
              : "0 2px 10px rgba(22,163,74,0.3)",
            marginBottom: 8,
          }}
        >
          {showInventory ? (
            <>
              <svg width="15" height="15" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              {isTestDriveConducted ? "Log & Rebook Test Drive" : "Book Test Drive"}
            </>
          ) : (
            <><SaveIcon />Save Disposition</>
          )}
        </button>


        {/* Call log ID footer */}
        {callLogId && (
          <div style={{ textAlign: "center", fontSize: 11, color: C.muted, paddingBottom: 12 }}>
            Call log&nbsp;
            <code style={{ background: "#f1f5f9", padding: "1px 6px", borderRadius: 4 }}>
              {callLogId.slice(0, 8)}…
            </code>
          </div>
        )}
      </div>
    </>
  );
}
